import { useMemo } from 'react';
import { Booking } from '@/types';
import { useScheduler, getDefaultStatusColor } from '@/contexts/SchedulerContext';
import { getContrastTextColor } from '@/lib/color-utils';

/**
 * Hook to get the dynamic color for a booking based on current settings.
 * This ensures that when status colors are changed in admin settings,
 * all bookings automatically reflect the new colors.
 * 
 * Priority order:
 * 1. Color override (if booking has a specific color override selected)
 * 2. Current status color from settings (dynamically looked up)
 * 3. Fallback to stored booking color (for backward compatibility)
 */
export const useBookingColor = (booking: Booking): string => {
  const { settings } = useScheduler();

  return useMemo(() => {
    // If booking has a stored color that matches a color override, use it
    // This handles the case where user explicitly selected a color override
    const matchingColorOverride = settings.colorOverrides?.find(
      co => co.color === booking.color
    );
    
    if (matchingColorOverride) {
      // User explicitly chose a color override, respect that choice
      return booking.color;
    }

    // Otherwise, dynamically look up the current status color
    const statusConfig = settings.statusConfigs?.find(
      s => String(s.id) === String(booking.status)
    );

    if (statusConfig?.color) {
      // Use the current status color from settings
      // This ensures retroactive updates when admin changes status colors
      return statusConfig.color;
    }

    // Fallback to stored booking color, then to default status color
    return booking.color || getDefaultStatusColor(booking.status);
  }, [booking.color, booking.status, settings.colorOverrides, settings.statusConfigs]);
};

/**
 * Hook to get the WCAG-compliant text colour for a booking.
 * Always returns '#000000' — the booking palette uses light pastels throughout,
 * so dark text gives consistent contrast without mixing black and white labels.
 */
export const useBookingTextColor = (booking: Booking): '#000000' => {
  const { settings } = useScheduler();

  return useMemo(() => {
    const matchingColorOverride = settings.colorOverrides?.find(
      co => co.color === booking.color
    );
    let bgColor: string;
    if (matchingColorOverride) {
      bgColor = booking.color;
    } else {
      const statusConfig = settings.statusConfigs?.find(
        s => String(s.id) === String(booking.status)
      );
      bgColor = statusConfig?.color || booking.color || getDefaultStatusColor(booking.status);
    }
    return getContrastTextColor(bgColor);
  }, [booking.color, booking.status, settings.colorOverrides, settings.statusConfigs]);
};

/**
 * Utility function to get booking color without hook (for use in non-component contexts)
 */
export const getBookingColor = (
  booking: Booking,
  statusConfigs: any[],
  colorOverrides: any[]
): string => {
  // Check if booking color matches a color override
  const matchingColorOverride = colorOverrides?.find(
    co => co.color === booking.color
  );
  
  if (matchingColorOverride) {
    return booking.color;
  }

  // Look up current status color
  const statusConfig = statusConfigs?.find(
    s => String(s.id) === String(booking.status)
  );

  if (statusConfig?.color) {
    return statusConfig.color;
  }

  // Fallback to stored booking color, then to default status color
  return booking.color || getDefaultStatusColor(booking.status);
};

/**
 * Utility function to get WCAG-compliant text colour without hook
 * (for use in non-component contexts such as CalendarGrid, MonthView, etc.)
 * Always returns '#000000' — consistent dark text across all pastel booking colours.
 */
export const getBookingTextColor = (
  booking: Booking,
  statusConfigs: any[],
  colorOverrides: any[]
): '#000000' => {
  const bgColor = getBookingColor(booking, statusConfigs, colorOverrides);
  return getContrastTextColor(bgColor);
};
